import { FormEvent, useCallback, useEffect, useState, type ReactNode } from 'react';
import { toast } from 'react-toastify';
import { PageCard } from '../layout/page/PageCard';
import { CollapsibleFormSection } from '../ui/CollapsibleFormSection';
import { DeleteIconButton } from '../ui/DeleteIconButton';
import { DraggableModalPanel } from '../ui/DraggableModalPanel';
import { EditIconButton } from '../ui/EditIconButton';
import { MeetingParticipantsIconButton } from '../ui/MeetingParticipantsIconButton';
import { FieldLabel } from '../ui/FieldLabel';
import { PlanLinkIconButton } from '../ui/PlanLinkIconButton';
import { FormActionBar } from '../ui/FormActionBar';
import { InlineAlert } from '../ui/InlineAlert';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { RequiredMark } from '../ui/RequiredMark';
import { CollaboratorAutocomplete } from '../ui/CollaboratorAutocomplete';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import {
  approveProjectChangeRequest,
  createProjectBacklogItem,
  createProjectChangeRequest,
  createProjectIssue,
  createProjectMeeting,
  createProjectRequirement,
  createProjectRisk,
  createProjectRoadmapItem,
  createProjectStakeholder,
  deleteProjectBacklogItem,
  deleteProjectChangeRequest,
  deleteProjectIssue,
  deleteProjectMeeting,
  deleteProjectRequirement,
  deleteProjectRisk,
  deleteProjectRoadmapItem,
  deleteProjectStakeholder,
  linkProjectBacklogRequirement,
  listProjectBacklogItems,
  listProjectBacklogRequirements,
  listProjectRequirements,
  listProjectChangeRequests,
  listProjectIssues,
  listProjectMeetings,
  listProjectRisks,
  listProjectRoadmapItems,
  listProjectStakeholders,
  ProjectBacklogItem,
  ProjectBacklogItemUpsertPayload,
  ProjectChangeRequest,
  ProjectChangeRequestUpsertPayload,
  ProjectIssue,
  ProjectIssueUpsertPayload,
  ProjectMeeting,
  ProjectMeetingUpsertPayload,
  ProjectRequirement,
  ProjectRequirementUpsertPayload,
  ProjectRisk,
  ProjectRiskUpsertPayload,
  ProjectRoadmapItem,
  ProjectRoadmapItemUpsertPayload,
  ProjectStakeholder,
  ProjectStakeholderUpsertPayload,
  rejectProjectChangeRequest,
  submitProjectChangeRequest,
  updateProjectBacklogItem,
  updateProjectChangeRequest,
  updateProjectIssue,
  updateProjectMeeting,
  unlinkProjectBacklogRequirement,
  updateProjectRequirement,
  updateProjectRisk,
  updateProjectRoadmapItem,
  updateProjectStakeholder,
} from '../../services/commercialProjectsApi';
import { RelatedRequirementsSection } from '../requirements/RelatedRequirementsSection';
import { formatDateBr, formatDateTimeBr } from '../../utils/formatDateBr';
import { fromLocalDateTimeInputValue, toLocalDateTimeInputValue } from '../../utils/localDateTimeInput';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';
import { MeetingParticipantsDialog } from './MeetingParticipantsDialog';
import { MeetingParticipantsField } from './MeetingParticipantsField';
import { ProjectBacklogExternalLinksPanel } from './ProjectBacklogExternalLinksPanel';
import { ProjectFilesSection } from './ProjectFilesSection';

export type WorkspaceTabId =
  | 'stakeholders'
  | 'requisitos'
  | 'backlog'
  | 'roadmap'
  | 'riscos'
  | 'problemas'
  | 'mudancas'
  | 'reunioes';

type ProjectWorkspacePanelProps = {
  projectId: string;
  canWrite: boolean;
  activeTab: WorkspaceTabId;
};

const INFLUENCE_OPTIONS = [
  { value: 'Low', label: 'Baixa' },
  { value: 'Medium', label: 'Média' },
  { value: 'High', label: 'Alta' },
  { value: 'Critical', label: 'Crítica' },
];

const INTEREST_OPTIONS = [
  { value: 'Low', label: 'Baixo' },
  { value: 'Medium', label: 'Médio' },
  { value: 'High', label: 'Alto' },
];

const COMMITMENT_OPTIONS = [
  { value: 'Pending', label: 'Pendente' },
  { value: 'Committed', label: 'Comprometido' },
  { value: 'Declined', label: 'Recusado' },
  { value: 'Withdrawn', label: 'Retirado' },
];

const PRIORITY_OPTIONS = [
  { value: 'Low', label: 'Baixa' },
  { value: 'Medium', label: 'Média' },
  { value: 'High', label: 'Alta' },
  { value: 'Critical', label: 'Crítica' },
];

const BACKLOG_STATUS_OPTIONS = [
  { value: 'Idea', label: 'Ideia' },
  { value: 'Ready', label: 'Pronto' },
  { value: 'InProgress', label: 'Em andamento' },
  { value: 'Validation', label: 'Em validação' },
  { value: 'Done', label: 'Concluído' },
  { value: 'Cancelled', label: 'Cancelado' },
];

const APPROVER_OBLIGATION_OPTIONS = [
  { value: 'Required', label: 'Obrigatório' },
  { value: 'Optional', label: 'Opcional' },
];

const ROADMAP_KIND_OPTIONS = [
  { value: 'Phase', label: 'Fase' },
  { value: 'Milestone', label: 'Marco' },
  { value: 'Checkpoint', label: 'Checkpoint' },
  { value: 'Release', label: 'Release' },
];

const ROADMAP_STATUS_OPTIONS = [
  { value: 'Planned', label: 'Planejado' },
  { value: 'InProgress', label: 'Em andamento' },
  { value: 'Achieved', label: 'Atingido' },
  { value: 'Missed', label: 'Perdido' },
  { value: 'Cancelled', label: 'Cancelado' },
];

const RISK_PROBABILITY_OPTIONS = [
  { value: 'Low', label: 'Baixa' },
  { value: 'Medium', label: 'Média' },
  { value: 'High', label: 'Alta' },
  { value: 'VeryHigh', label: 'Muito alta' },
];

const RISK_IMPACT_OPTIONS = [
  { value: 'Low', label: 'Baixo' },
  { value: 'Medium', label: 'Médio' },
  { value: 'High', label: 'Alto' },
  { value: 'Critical', label: 'Crítico' },
];

const RISK_STATUS_OPTIONS = [
  { value: 'Identified', label: 'Identificado' },
  { value: 'Analyzing', label: 'Em análise' },
  { value: 'Mitigating', label: 'Mitigando' },
  { value: 'Monitoring', label: 'Monitorando' },
  { value: 'Closed', label: 'Encerrado' },
  { value: 'Materialized', label: 'Materializado' },
];

const ISSUE_SEVERITY_OPTIONS = [
  { value: 'Low', label: 'Baixa' },
  { value: 'Medium', label: 'Média' },
  { value: 'High', label: 'Alta' },
  { value: 'Critical', label: 'Crítica' },
];

const ISSUE_STATUS_OPTIONS = [
  { value: 'Open', label: 'Aberto' },
  { value: 'InAnalysis', label: 'Em análise' },
  { value: 'ActionTaken', label: 'Ação tomada' },
  { value: 'Resolved', label: 'Resolvido' },
  { value: 'Closed', label: 'Encerrado' },
];

const CHANGE_STATUS_LABELS: Record<string, string> = {
  Draft: 'Rascunho',
  Submitted: 'Submetido',
  UnderReview: 'Em análise',
  Approved: 'Aprovado',
  Rejected: 'Rejeitado',
  Implemented: 'Implementado',
  Cancelled: 'Cancelado',
};

const MEETING_TYPE_OPTIONS = [
  { value: 'KickOff', label: 'Kick-off' },
  { value: 'Planning', label: 'Planejamento' },
  { value: 'FollowUp', label: 'Acompanhamento' },
  { value: 'Requirements', label: 'Requisitos' },
  { value: 'Changes', label: 'Mudanças' },
  { value: 'Other', label: 'Outro' },
];

function toDateInput(value?: string | null): string {
  return value ? value.slice(0, 10) : '';
}

function approverDecisionLabel(decision?: string): string {
  if (decision === 'Approved') return 'aprovada';
  if (decision === 'Returned') return 'devolvida';
  if (decision === 'Pending') return 'pendente';
  return '';
}

function labelFor(options: { value: string; label: string }[], value: string): string {
  return options.find((option) => option.value === value)?.label ?? value;
}

function truncateText(value: string, maxLength: number): string {
  const text = value.trim();
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trimEnd()}…`;
}

function formatMeetingDateTime(value?: string | null): string {
  return formatDateTimeBr(value).replace(/,\s*/, ' ');
}

const emptyStakeholderForm = (): ProjectStakeholderUpsertPayload => ({
  name: '',
  influence: 'Medium',
  interest: 'Medium',
  commitmentStatus: 'Pending',
  appUserId: null,
});

const emptyBacklogForm = (): ProjectBacklogItemUpsertPayload => ({
  title: '',
  priority: 'Medium',
  status: 'Idea',
  sortOrder: 0,
  clientVisible: false,
  assigneeUserId: null,
  approvers: [],
});

const emptyRoadmapForm = (): ProjectRoadmapItemUpsertPayload => ({
  title: '',
  kind: 'Milestone',
  status: 'Planned',
  sortOrder: 0,
});

const emptyRiskForm = (): ProjectRiskUpsertPayload => ({
  title: '',
  probability: 'Medium',
  impact: 'Medium',
  treatmentPriority: 0,
  status: 'Identified',
  identifiedAtUtc: new Date().toISOString(),
});

const emptyIssueForm = (): ProjectIssueUpsertPayload => ({
  title: '',
  severity: 'Medium',
  status: 'Open',
  identifiedAtUtc: new Date().toISOString(),
});

const emptyChangeForm = (): ProjectChangeRequestUpsertPayload => ({
  title: '',
});

const emptyRequirementForm = (): ProjectRequirementUpsertPayload => ({
  title: '',
  validationCriteria: '',
});

const emptyMeetingForm = (): ProjectMeetingUpsertPayload & { scheduledAtLocal: string } => ({
  title: '',
  meetingType: 'FollowUp',
  scheduledAtUtc: new Date().toISOString(),
  scheduledAtLocal: toLocalDateTimeInputValue(new Date().toISOString()),
  minutes: '',
  stakeholderIds: [],
});

const TAB_FORM_TITLES: Record<WorkspaceTabId, { create: string; edit: string; summary: string }> = {
  stakeholders: {
    create: 'Novo stakeholder',
    edit: 'Editar stakeholder',
    summary: 'Clique para cadastrar um novo stakeholder',
  },
  requisitos: {
    create: 'Novo requisito',
    edit: 'Editar requisito',
    summary: 'Clique para cadastrar um novo requisito',
  },
  backlog: {
    create: 'Novo item de backlog',
    edit: 'Editar item de backlog',
    summary: 'Clique para cadastrar um novo item',
  },
  roadmap: {
    create: 'Novo item de roadmap',
    edit: 'Editar item de roadmap',
    summary: 'Clique para cadastrar um novo item',
  },
  riscos: {
    create: 'Novo risco',
    edit: 'Editar risco',
    summary: 'Clique para cadastrar um novo risco',
  },
  problemas: {
    create: 'Novo problema',
    edit: 'Editar problema',
    summary: 'Clique para cadastrar um novo problema',
  },
  mudancas: {
    create: 'Nova mudança',
    edit: 'Editar mudança',
    summary: 'Clique para cadastrar uma nova mudança',
  },
  reunioes: {
    create: 'Nova reunião',
    edit: 'Editar reunião',
    summary: 'Clique para cadastrar uma nova reunião',
  },
};

export function ProjectWorkspacePanel({ projectId, canWrite, activeTab }: ProjectWorkspacePanelProps) {
  const { confirm } = useConfirmDialog();
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } =
    useCollapsibleItemForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [linkPanelItemId, setLinkPanelItemId] = useState<string | null>(null);
  const [participantsMeetingId, setParticipantsMeetingId] = useState<string | null>(null);
  const [backlogAssigneeName, setBacklogAssigneeName] = useState<string | null>(null);
  const [stakeholderUserName, setStakeholderUserName] = useState<string | null>(null);

  const [stakeholders, setStakeholders] = useState<ProjectStakeholder[]>([]);
  const [requirements, setRequirements] = useState<ProjectRequirement[]>([]);
  const [backlogItems, setBacklogItems] = useState<ProjectBacklogItem[]>([]);
  const [roadmapItems, setRoadmapItems] = useState<ProjectRoadmapItem[]>([]);
  const [risks, setRisks] = useState<ProjectRisk[]>([]);
  const [issues, setIssues] = useState<ProjectIssue[]>([]);
  const [changes, setChanges] = useState<ProjectChangeRequest[]>([]);
  const [meetings, setMeetings] = useState<ProjectMeeting[]>([]);

  const [stakeholderForm, setStakeholderForm] = useState(emptyStakeholderForm);
  const [requirementForm, setRequirementForm] = useState(emptyRequirementForm);
  const [backlogForm, setBacklogForm] = useState(emptyBacklogForm);
  const [roadmapForm, setRoadmapForm] = useState(emptyRoadmapForm);
  const [riskForm, setRiskForm] = useState(emptyRiskForm);
  const [issueForm, setIssueForm] = useState(emptyIssueForm);
  const [changeForm, setChangeForm] = useState(emptyChangeForm);
  const [meetingForm, setMeetingForm] = useState(emptyMeetingForm);

  const resetForm = useCallback(() => {
    setEditingId(null);
    setStakeholderForm(emptyStakeholderForm());
    setStakeholderUserName(null);
    setRequirementForm(emptyRequirementForm());
    setBacklogForm(emptyBacklogForm());
    setBacklogAssigneeName(null);
    setRoadmapForm(emptyRoadmapForm());
    setRiskForm(emptyRiskForm());
    setIssueForm(emptyIssueForm());
    setChangeForm(emptyChangeForm());
    setMeetingForm(emptyMeetingForm());
  }, []);

  const loadItems = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      switch (activeTab) {
        case 'stakeholders':
          setStakeholders(await listProjectStakeholders(projectId));
          break;
        case 'requisitos':
          setRequirements(await listProjectRequirements(projectId));
          break;
        case 'backlog': {
          const [items, people] = await Promise.all([
            listProjectBacklogItems(projectId),
            listProjectStakeholders(projectId),
          ]);
          setBacklogItems(items);
          setStakeholders(people);
          break;
        }
        case 'roadmap':
          setRoadmapItems(await listProjectRoadmapItems(projectId));
          break;
        case 'riscos':
          setRisks(await listProjectRisks(projectId));
          break;
        case 'problemas':
          setIssues(await listProjectIssues(projectId));
          break;
        case 'mudancas':
          setChanges(await listProjectChangeRequests(projectId));
          break;
        case 'reunioes': {
          const [items, people] = await Promise.all([
            listProjectMeetings(projectId),
            listProjectStakeholders(projectId),
          ]);
          setMeetings(items);
          setStakeholders(people);
          break;
        }
      }
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao carregar dados.'));
    } finally {
      setLoading(false);
    }
  }, [activeTab, projectId]);

  useEffect(() => {
    collapseForm(resetForm);
    setParticipantsMeetingId(null);
    void loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recarrega ao trocar aba/projeto
  }, [loadItems, resetForm]);

  if (!projectId) {
    return <InlineAlert>Selecione ou crie um projeto para gerenciar esta área.</InlineAlert>;
  }

  async function handleDelete(
    label: string,
    deleteFn: () => Promise<void>,
  ): Promise<void> {
    const confirmed = await confirm({
      title: 'Excluir',
      message: `Excluir ${label}?`,
      confirmLabel: 'Excluir',
      tone: 'danger',
    });
    if (!confirmed) return;
    try {
      await deleteFn();
      toast.success('Registro excluído.');
      resetForm();
      await loadItems();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao excluir.'));
    }
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;
    setSaving(true);
    try {
      switch (activeTab) {
        case 'stakeholders': {
          if (!stakeholderForm.name.trim()) {
            toast.error('Informe o nome do stakeholder.');
            return;
          }
          if (editingId) {
            await updateProjectStakeholder(projectId, editingId, stakeholderForm);
            toast.success('Stakeholder atualizado.');
          } else {
            await createProjectStakeholder(projectId, stakeholderForm);
            toast.success('Stakeholder criado.');
          }
          break;
        }
        case 'backlog': {
          if (!backlogForm.title.trim()) {
            toast.error('Informe o título.');
            return;
          }
          const approvers = backlogForm.approvers ?? [];
          if (backlogForm.status === 'Validation') {
            if (!backlogForm.clientVisible) {
              toast.error('Marque o item como visível ao cliente para pedir aprovação.');
              return;
            }
            if (approvers.some((approver) => !approver.contactId)) {
              toast.error('Selecione o contato de cada aprovador.');
              return;
            }
            if (!approvers.some((approver) => approver.obligation === 'Required')) {
              toast.error('Informe ao menos um aprovador obrigatório.');
              return;
            }
          }
          const payload: ProjectBacklogItemUpsertPayload = {
            ...backlogForm,
            clientVisible: backlogForm.clientVisible === true,
            approvers: approvers.map(({ contactId, obligation }) => ({ contactId, obligation })),
          };
          if (editingId) {
            await updateProjectBacklogItem(projectId, editingId, payload);
            toast.success('Item atualizado.');
          } else {
            await createProjectBacklogItem(projectId, payload);
            toast.success('Item criado.');
          }
          break;
        }
        case 'requisitos': {
          if (!requirementForm.title.trim()) {
            toast.error('Informe o que a solução precisa atender.');
            return;
          }
          if (!requirementForm.validationCriteria.trim()) {
            toast.error('Informe como o requisito será validado.');
            return;
          }
          if (editingId) {
            await updateProjectRequirement(projectId, editingId, requirementForm);
            toast.success('Requisito atualizado.');
          } else {
            await createProjectRequirement(projectId, requirementForm);
            toast.success('Requisito criado.');
          }
          break;
        }
        case 'roadmap': {
          if (!roadmapForm.title.trim()) {
            toast.error('Informe o título.');
            return;
          }
          const payload = {
            ...roadmapForm,
            plannedDateUtc: roadmapForm.plannedDateUtc || null,
          };
          if (editingId) {
            await updateProjectRoadmapItem(projectId, editingId, payload);
            toast.success('Item atualizado.');
          } else {
            await createProjectRoadmapItem(projectId, payload);
            toast.success('Item criado.');
          }
          break;
        }
        case 'riscos': {
          if (!riskForm.title.trim()) {
            toast.error('Informe o título.');
            return;
          }
          if (editingId) {
            await updateProjectRisk(projectId, editingId, riskForm);
            toast.success('Risco atualizado.');
          } else {
            await createProjectRisk(projectId, riskForm);
            toast.success('Risco criado.');
          }
          break;
        }
        case 'problemas': {
          if (!issueForm.title.trim()) {
            toast.error('Informe o título.');
            return;
          }
          if (editingId) {
            await updateProjectIssue(projectId, editingId, issueForm);
            toast.success('Problema atualizado.');
          } else {
            await createProjectIssue(projectId, issueForm);
            toast.success('Problema criado.');
          }
          break;
        }
        case 'mudancas': {
          if (!changeForm.title.trim()) {
            toast.error('Informe o título.');
            return;
          }
          if (editingId) {
            await updateProjectChangeRequest(projectId, editingId, changeForm);
            toast.success('Solicitação atualizada.');
          } else {
            await createProjectChangeRequest(projectId, changeForm);
            toast.success('Solicitação criada.');
          }
          break;
        }
        case 'reunioes': {
          if (!meetingForm.title.trim()) {
            toast.error('Informe o título.');
            return;
          }
          if (meetingForm.meetingType === 'Other' && !meetingForm.otherTypeNote?.trim()) {
            toast.error('Informe a descrição do tipo de reunião.');
            return;
          }
          const scheduledAtUtc = fromLocalDateTimeInputValue(meetingForm.scheduledAtLocal);
          if (!scheduledAtUtc) {
            toast.error('Informe data e hora válidas.');
            return;
          }
          const payload: ProjectMeetingUpsertPayload = {
            title: meetingForm.title,
            meetingType: meetingForm.meetingType,
            otherTypeNote: meetingForm.otherTypeNote,
            scheduledAtUtc,
            durationMinutes: meetingForm.durationMinutes,
            locationOrLink: meetingForm.locationOrLink,
            stakeholderIds: meetingForm.stakeholderIds ?? [],
            ...(editingId ? { minutes: meetingForm.minutes } : {}),
          };
          if (editingId) {
            await updateProjectMeeting(projectId, editingId, payload);
            toast.success('Reunião atualizada.');
          } else {
            await createProjectMeeting(projectId, payload);
            toast.success('Reunião criada.');
          }
          break;
        }
      }
      handleCreateSuccess(resetForm);
      await loadItems();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao salvar.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleChangeAction(
    id: string,
    action: 'submit' | 'approve' | 'reject',
  ): Promise<void> {
    if (!canWrite) return;
    try {
      if (action === 'submit') {
        await submitProjectChangeRequest(projectId, id);
        toast.success('Solicitação submetida.');
      } else if (action === 'approve') {
        await approveProjectChangeRequest(projectId, id);
        toast.success('Solicitação aprovada.');
      } else {
        const reason = window.prompt('Motivo da rejeição');
        if (!reason?.trim()) return;
        await rejectProjectChangeRequest(projectId, id, { rejectionReason: reason.trim() });
        toast.success('Solicitação rejeitada.');
      }
      await loadItems();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha na ação.'));
    }
  }

  function renderForm(): ReactNode {
    if (!canWrite) return null;

    switch (activeTab) {
      case 'stakeholders':
        return (
          <div className="grid gap-2 md:grid-cols-3">
            <label className="erp-label">
              Nome <RequiredMark />
              <input
                className="erp-input mt-1"
                required
                value={stakeholderForm.name}
                onChange={(e) => setStakeholderForm((c) => ({ ...c, name: e.target.value }))}
              />
            </label>
            <label className="erp-label">
              Papel
              <input
                className="erp-input mt-1"
                value={stakeholderForm.role ?? ''}
                onChange={(e) => setStakeholderForm((c) => ({ ...c, role: e.target.value }))}
              />
            </label>
            <label className="erp-label">
              Organização
              <input
                className="erp-input mt-1"
                value={stakeholderForm.organization ?? ''}
                onChange={(e) => setStakeholderForm((c) => ({ ...c, organization: e.target.value }))}
              />
            </label>
            <label className="erp-label">
              Influência
              <select
                className="erp-input mt-1"
                value={stakeholderForm.influence}
                onChange={(e) => setStakeholderForm((c) => ({ ...c, influence: e.target.value }))}
              >
                {INFLUENCE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            <label className="erp-label">
              Interesse
              <select
                className="erp-input mt-1"
                value={stakeholderForm.interest}
                onChange={(e) => setStakeholderForm((c) => ({ ...c, interest: e.target.value }))}
              >
                {INTEREST_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            <label className="erp-label">
              Compromisso
              <select
                className="erp-input mt-1"
                value={stakeholderForm.commitmentStatus}
                onChange={(e) => setStakeholderForm((c) => ({ ...c, commitmentStatus: e.target.value }))}
              >
                {COMMITMENT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            <label className="erp-label md:col-span-2">
              <FieldLabel description="Pessoa com perfil Cliente que entra no portal deste projeto.">
                Usuário de acesso
              </FieldLabel>
              <div className="mt-1">
                <CollaboratorAutocomplete
                  valueUserId={stakeholderForm.appUserId}
                  valueUserName={stakeholderUserName}
                  allowClear
                  emptyLabel="Sem usuário vinculado"
                  clearLabel="Remover usuário"
                  onCommit={(user) => {
                    setStakeholderForm((current) => ({
                      ...current,
                      appUserId: user?.userId ?? null,
                    }));
                    setStakeholderUserName(user?.name ?? null);
                  }}
                />
              </div>
            </label>
          </div>
        );

      case 'backlog': {
        const approvers = backlogForm.approvers ?? [];
        const contactStakeholders = stakeholders.filter((person) => Boolean(person.contactId));
        const usedContactIds = new Set(approvers.map((approver) => approver.contactId));
        const unusedStakeholder = contactStakeholders.find(
          (person) => person.contactId && !usedContactIds.has(person.contactId),
        );
        return (
          <div className="grid gap-2 md:grid-cols-3">
            <label className="erp-label md:col-span-2">
              Título <RequiredMark />
              <input
                className="erp-input mt-1"
                required
                value={backlogForm.title}
                onChange={(e) => setBacklogForm((c) => ({ ...c, title: e.target.value }))}
              />
            </label>
            <label className="erp-label">
              Prioridade
              <select
                className="erp-input mt-1"
                value={backlogForm.priority}
                onChange={(e) => setBacklogForm((c) => ({ ...c, priority: e.target.value }))}
              >
                {PRIORITY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            <label className="erp-label">
              Status
              <select
                className="erp-input mt-1"
                value={backlogForm.status}
                onChange={(e) => {
                  const status = e.target.value;
                  setBacklogForm((c) => ({
                    ...c,
                    status,
                    clientVisible: status === 'Validation' ? true : c.clientVisible,
                  }));
                }}
              >
                {BACKLOG_STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            <label className="erp-label md:col-span-2">
              Responsável
              <div className="mt-1">
                <CollaboratorAutocomplete
                  valueUserId={backlogForm.assigneeUserId}
                  valueUserName={backlogAssigneeName}
                  allowClear
                  emptyLabel="Não atribuído"
                  clearLabel="Remover responsável"
                  onCommit={(user) => {
                    setBacklogForm((current) => ({
                      ...current,
                      assigneeUserId: user?.userId ?? null,
                    }));
                    setBacklogAssigneeName(user?.name ?? null);
                  }}
                />
              </div>
            </label>
            <label className="erp-label md:col-span-3">
              Descrição
              <input
                className="erp-input mt-1"
                value={backlogForm.description ?? ''}
                onChange={(e) => setBacklogForm((c) => ({ ...c, description: e.target.value }))}
              />
            </label>
            {backlogForm.status === 'Validation' ? (
              <div className="space-y-2 rounded-md border border-secondary-200 p-3 md:col-span-3">
                <label className="flex items-center gap-2 text-sm text-secondary-800">
                  <input
                    type="checkbox"
                    checked={backlogForm.clientVisible === true}
                    onChange={(e) => setBacklogForm((c) => ({ ...c, clientVisible: e.target.checked }))}
                  />
                  Visível ao cliente
                </label>
                <p className="text-xs text-secondary-500">
                  Cada aprovador obrigatório precisa decidir. O opcional pode votar, e o silêncio dele não trava a demanda.
                </p>
                {approvers.map((approver, index) => {
                  const options = contactStakeholders.filter(
                    (person) => person.contactId === approver.contactId || (person.contactId && !usedContactIds.has(person.contactId)),
                  );
                  const decision = approverDecisionLabel(approver.decision);
                  return (
                    <div key={`${approver.contactId}-${index}`} className="grid gap-2 md:grid-cols-[1fr_180px_auto] md:items-end">
                      <label className="erp-label">
                        Aprovador
                        <select
                          className="erp-input mt-1"
                          value={approver.contactId}
                          onChange={(e) => {
                            const contactId = e.target.value;
                            setBacklogForm((c) => ({
                              ...c,
                              approvers: (c.approvers ?? []).map((row, rowIndex) =>
                                rowIndex === index ? { ...row, contactId } : row,
                              ),
                            }));
                          }}
                        >
                          <option value="">Selecione</option>
                          {approver.contactId && !options.some((person) => person.contactId === approver.contactId) ? (
                            <option value={approver.contactId}>{approver.contactName || 'Contato'}</option>
                          ) : null}
                          {options.map((person) => (
                            <option key={person.id} value={person.contactId ?? ''}>{person.name}</option>
                          ))}
                        </select>
                      </label>
                      <label className="erp-label">
                        Participação
                        <select
                          className="erp-input mt-1"
                          value={approver.obligation}
                          onChange={(e) => {
                            const obligation = e.target.value;
                            setBacklogForm((c) => ({
                              ...c,
                              approvers: (c.approvers ?? []).map((row, rowIndex) =>
                                rowIndex === index ? { ...row, obligation } : row,
                              ),
                            }));
                          }}
                        >
                          {APPROVER_OBLIGATION_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </label>
                      <div className="flex items-center gap-2 pb-1">
                        {decision ? <span className="text-xs text-secondary-500">{decision}</span> : null}
                        <button
                          type="button"
                          className="rounded border border-secondary-300 px-2 py-1 text-xs hover:bg-secondary-50"
                          onClick={() =>
                            setBacklogForm((c) => ({
                              ...c,
                              approvers: (c.approvers ?? []).filter((_, rowIndex) => rowIndex !== index),
                            }))
                          }
                        >
                          Remover
                        </button>
                      </div>
                    </div>
                  );
                })}
                <button
                  type="button"
                  className="rounded border border-secondary-300 px-2 py-1 text-xs hover:bg-secondary-50 disabled:opacity-50"
                  disabled={!unusedStakeholder}
                  onClick={() => {
                    const contactId = unusedStakeholder?.contactId;
                    if (!contactId) return;
                    setBacklogForm((c) => ({
                      ...c,
                      approvers: [
                        ...(c.approvers ?? []),
                        { contactId, obligation: 'Required' },
                      ],
                    }));
                  }}
                >
                  Adicionar aprovador
                </button>
                {contactStakeholders.length === 0 ? (
                  <p className="text-xs text-secondary-500">
                    Cadastre um stakeholder com contato vinculado para pedir aprovação.
                  </p>
                ) : null}
              </div>
            ) : null}
            {editingId ? (
              <RelatedRequirementsSection
                sourceKey={editingId}
                canWrite={canWrite}
                emptyCatalogMessage="Nenhum requisito deste projeto. Cadastre na aba Requisitos e volte para associar."
                loadCatalog={() => listProjectRequirements(projectId)}
                loadLinked={() => listProjectBacklogRequirements(projectId, editingId)}
                onLink={(requirementId) => linkProjectBacklogRequirement(projectId, editingId, requirementId).then(() => undefined)}
                onUnlink={(requirementId) => unlinkProjectBacklogRequirement(projectId, editingId, requirementId)}
              />
            ) : (
              <p className="text-xs text-secondary-500 md:col-span-3">
                Salve o item para associar requisitos já cadastrados na aba Requisitos.
              </p>
            )}
          </div>
        );
      }

      case 'requisitos':
        return (
          <div className="grid gap-2 md:grid-cols-1">
            <label className="erp-label">
              O que precisa atender <RequiredMark />
              <input
                className="erp-input mt-1"
                required
                maxLength={200}
                value={requirementForm.title}
                onChange={(e) => setRequirementForm((c) => ({ ...c, title: e.target.value }))}
              />
            </label>
            <label className="erp-label">
              Como será validado <RequiredMark />
              <textarea
                className="erp-input mt-1"
                required
                rows={3}
                maxLength={4000}
                value={requirementForm.validationCriteria}
                onChange={(e) => setRequirementForm((c) => ({ ...c, validationCriteria: e.target.value }))}
              />
            </label>
          </div>
        );

      case 'roadmap':
        return (
          <div className="grid gap-2 md:grid-cols-4">
            <label className="erp-label md:col-span-2">
              Título <RequiredMark />
              <input
                className="erp-input mt-1"
                required
                value={roadmapForm.title}
                onChange={(e) => setRoadmapForm((c) => ({ ...c, title: e.target.value }))}
              />
            </label>
            <label className="erp-label">
              Tipo
              <select
                className="erp-input mt-1"
                value={roadmapForm.kind}
                onChange={(e) => setRoadmapForm((c) => ({ ...c, kind: e.target.value }))}
              >
                {ROADMAP_KIND_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            <label className="erp-label">
              Status
              <select
                className="erp-input mt-1"
                value={roadmapForm.status}
                onChange={(e) => setRoadmapForm((c) => ({ ...c, status: e.target.value }))}
              >
                {ROADMAP_STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            <label className="erp-label">
              Data planejada
              <input
                className="erp-input mt-1"
                type="date"
                value={toDateInput(roadmapForm.plannedDateUtc)}
                onChange={(e) =>
                  setRoadmapForm((c) => ({
                    ...c,
                    plannedDateUtc: e.target.value ? `${e.target.value}T00:00:00.000Z` : null,
                  }))
                }
              />
            </label>
          </div>
        );

      case 'riscos':
        return (
          <div className="grid gap-2 md:grid-cols-4">
            <label className="erp-label md:col-span-2">
              Título <RequiredMark />
              <input
                className="erp-input mt-1"
                required
                value={riskForm.title}
                onChange={(e) => setRiskForm((c) => ({ ...c, title: e.target.value }))}
              />
            </label>
            <label className="erp-label">
              Probabilidade
              <select
                className="erp-input mt-1"
                value={riskForm.probability}
                onChange={(e) => setRiskForm((c) => ({ ...c, probability: e.target.value }))}
              >
                {RISK_PROBABILITY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            <label className="erp-label">
              Impacto
              <select
                className="erp-input mt-1"
                value={riskForm.impact}
                onChange={(e) => setRiskForm((c) => ({ ...c, impact: e.target.value }))}
              >
                {RISK_IMPACT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            <label className="erp-label">
              Status
              <select
                className="erp-input mt-1"
                value={riskForm.status}
                onChange={(e) => setRiskForm((c) => ({ ...c, status: e.target.value }))}
              >
                {RISK_STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            <label className="erp-label md:col-span-3">
              Plano de mitigação
              <input
                className="erp-input mt-1"
                value={riskForm.mitigationPlan ?? ''}
                onChange={(e) => setRiskForm((c) => ({ ...c, mitigationPlan: e.target.value }))}
              />
            </label>
          </div>
        );

      case 'problemas':
        return (
          <div className="grid gap-2 md:grid-cols-3">
            <label className="erp-label md:col-span-2">
              Título <RequiredMark />
              <input
                className="erp-input mt-1"
                required
                value={issueForm.title}
                onChange={(e) => setIssueForm((c) => ({ ...c, title: e.target.value }))}
              />
            </label>
            <label className="erp-label">
              Severidade
              <select
                className="erp-input mt-1"
                value={issueForm.severity}
                onChange={(e) => setIssueForm((c) => ({ ...c, severity: e.target.value }))}
              >
                {ISSUE_SEVERITY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            <label className="erp-label">
              Status
              <select
                className="erp-input mt-1"
                value={issueForm.status}
                onChange={(e) => setIssueForm((c) => ({ ...c, status: e.target.value }))}
              >
                {ISSUE_STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            <label className="erp-label md:col-span-2">
              Descrição
              <input
                className="erp-input mt-1"
                value={issueForm.description ?? ''}
                onChange={(e) => setIssueForm((c) => ({ ...c, description: e.target.value }))}
              />
            </label>
          </div>
        );

      case 'mudancas':
        return (
          <div className="grid gap-2 md:grid-cols-2">
            <label className="erp-label">
              Título <RequiredMark />
              <input
                className="erp-input mt-1"
                required
                value={changeForm.title}
                onChange={(e) => setChangeForm((c) => ({ ...c, title: e.target.value }))}
                disabled={Boolean(editingId && changes.find((c) => c.id === editingId)?.status !== 'Draft')}
              />
            </label>
            <label className="erp-label">
              Impacto no escopo
              <input
                className="erp-input mt-1"
                value={changeForm.impactScope ?? ''}
                onChange={(e) => setChangeForm((c) => ({ ...c, impactScope: e.target.value }))}
                disabled={Boolean(editingId && changes.find((c) => c.id === editingId)?.status !== 'Draft')}
              />
            </label>
            <label className="erp-label md:col-span-2">
              Descrição
              <textarea
                className="erp-input mt-1"
                rows={2}
                value={changeForm.description ?? ''}
                onChange={(e) => setChangeForm((c) => ({ ...c, description: e.target.value }))}
                disabled={Boolean(editingId && changes.find((c) => c.id === editingId)?.status !== 'Draft')}
              />
            </label>
          </div>
        );

      case 'reunioes':
        return (
          <div className="grid gap-2 md:grid-cols-4">
            <label className="erp-label md:col-span-2">
              Título <RequiredMark />
              <input
                className="erp-input mt-1"
                required
                value={meetingForm.title}
                onChange={(e) => setMeetingForm((c) => ({ ...c, title: e.target.value }))}
              />
            </label>
            <label className="erp-label">
              Tipo
              <select
                className="erp-input mt-1"
                value={meetingForm.meetingType}
                onChange={(e) => setMeetingForm((c) => ({ ...c, meetingType: e.target.value }))}
              >
                {MEETING_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            <label className="erp-label">
              Data/hora <RequiredMark />
              <input
                className="erp-input mt-1"
                type="datetime-local"
                required
                value={meetingForm.scheduledAtLocal}
                onChange={(e) => setMeetingForm((c) => ({ ...c, scheduledAtLocal: e.target.value }))}
              />
            </label>
            {meetingForm.meetingType === 'Other' ? (
              <label className="erp-label md:col-span-2">
                Descrição do tipo <RequiredMark />
                <input
                  className="erp-input mt-1"
                  required
                  value={meetingForm.otherTypeNote ?? ''}
                  onChange={(e) => setMeetingForm((c) => ({ ...c, otherTypeNote: e.target.value }))}
                />
              </label>
            ) : null}
            <label className="erp-label md:col-span-2">
              Local/link
              <input
                className="erp-input mt-1"
                placeholder="Sala, endereço ou link da reunião"
                value={meetingForm.locationOrLink ?? ''}
                onChange={(e) => setMeetingForm((c) => ({ ...c, locationOrLink: e.target.value }))}
              />
            </label>
            <MeetingParticipantsField
              projectId={projectId}
              canWrite={canWrite}
              stakeholders={stakeholders}
              selectedIds={meetingForm.stakeholderIds ?? []}
              onChange={(ids) => setMeetingForm((current) => ({ ...current, stakeholderIds: ids }))}
              onStakeholderCreated={(stakeholder) =>
                setStakeholders((current) =>
                  current.some((person) => person.id === stakeholder.id) ? current : [...current, stakeholder])
              }
            />
            {editingId ? (
              <div className="md:col-span-4 space-y-3">
                <label className="erp-label">
                  Ata
                  <textarea
                    className="erp-input mt-1"
                    rows={3}
                    maxLength={8000}
                    value={meetingForm.minutes ?? ''}
                    onChange={(e) => setMeetingForm((c) => ({ ...c, minutes: e.target.value }))}
                  />
                </label>
                <ProjectFilesSection
                  projectId={projectId}
                  meetingId={editingId}
                  canWrite={canWrite}
                  title="Documentos"
                  description="Envie um arquivo ou informe um link."
                />
              </div>
            ) : null}
          </div>
        );

      default:
        return null;
    }
  }

  function renderList(): ReactNode {
    switch (activeTab) {
      case 'stakeholders':
        if (stakeholders.length === 0) {
          return <InlineAlert>Nenhum stakeholder cadastrado. Use o formulário acima para incluir o primeiro.</InlineAlert>;
        }
        return (
          <div className="erp-table-wrap">
            <div className="erp-table-scroll">
              <table className="erp-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Papel</th>
                    <th>Organização</th>
                    <th>Influência</th>
                    <th>Interesse</th>
                    <th>Usuário</th>
                    {canWrite ? <th className="text-right">Ações</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {stakeholders.map((item) => {
                    const userName = item.appUserName?.trim() || 'Sem usuário vinculado';
                    return (
                      <tr key={item.id}>
                        <td className="max-w-md font-medium text-secondary-900" title={item.name}>
                          {truncateText(item.name, 200)}
                        </td>
                        <td>{item.role?.trim() || '—'}</td>
                        <td>{item.organization?.trim() || '—'}</td>
                        <td className="whitespace-nowrap">{labelFor(INFLUENCE_OPTIONS, item.influence)}</td>
                        <td className="whitespace-nowrap">{labelFor(INTEREST_OPTIONS, item.interest)}</td>
                        <td className={item.appUserName?.trim() ? 'text-secondary-800' : 'text-secondary-400'}>
                          {userName}
                        </td>
                        {canWrite ? (
                          <td>
                            <div className="flex justify-end gap-1">
                              <EditIconButton
                                onClick={() => {
                                  setEditingId(item.id);
                                  expandForm();
                                  setStakeholderUserName(item.appUserName ?? null);
                                  setStakeholderForm({
                                    contactId: item.contactId,
                                    name: item.name,
                                    role: item.role ?? '',
                                    organization: item.organization ?? '',
                                    email: item.email ?? '',
                                    phone: item.phone ?? '',
                                    influence: item.influence,
                                    interest: item.interest,
                                    commitmentStatus: item.commitmentStatus,
                                    engagementPlan: item.engagementPlan ?? '',
                                    notes: item.notes ?? '',
                                    appUserId: item.appUserId ?? null,
                                  });
                                }}
                              />
                              <DeleteIconButton
                                onClick={() =>
                                  void handleDelete(item.name, () => deleteProjectStakeholder(projectId, item.id))
                                }
                              />
                            </div>
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'backlog': {
        const linkPanelItem = linkPanelItemId
          ? backlogItems.find((item) => item.id === linkPanelItemId) ?? null
          : null;
        return (
          <>
            {backlogItems.length === 0 ? (
              <InlineAlert>Nenhum item de backlog. Use o formulário acima para cadastrar o primeiro.</InlineAlert>
            ) : (
              <div className="erp-table-wrap">
                <div className="erp-table-scroll">
                  <table className="erp-table">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Responsável</th>
                        <th>Prioridade</th>
                        <th>Status</th>
                        <th className="text-right">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {backlogItems.map((item) => {
                        const assigneeName = item.assigneeUserName?.trim() || 'Não atribuído';
                        const canPlanLink = canWrite || (item.externalLinks?.length ?? 0) > 0;
                        return (
                          <tr key={item.id}>
                            <td className="max-w-md font-medium text-secondary-900" title={item.title}>
                              {truncateText(item.title, 200)}
                            </td>
                            <td className={item.assigneeUserName?.trim() ? 'text-secondary-800' : 'text-secondary-400'}>
                              {assigneeName}
                            </td>
                            <td className="whitespace-nowrap">{labelFor(PRIORITY_OPTIONS, item.priority)}</td>
                            <td className="whitespace-nowrap">{labelFor(BACKLOG_STATUS_OPTIONS, item.status)}</td>
                            <td>
                              <div className="flex justify-end gap-1">
                                {canPlanLink ? (
                                  <PlanLinkIconButton
                                    label={canWrite ? 'Planejar vínculo' : 'Ver vínculos'}
                                    onClick={() => setLinkPanelItemId(item.id)}
                                  />
                                ) : null}
                                {canWrite ? (
                                  <>
                                    <EditIconButton
                                      onClick={() => {
                                        setEditingId(item.id);
                                        expandForm();
                                        setBacklogAssigneeName(item.assigneeUserName ?? null);
                                        setBacklogForm({
                                          title: item.title,
                                          description: item.description ?? '',
                                          priority: item.priority,
                                          status: item.status,
                                          sortOrder: item.sortOrder,
                                          tags: item.tags ?? '',
                                          clientVisible: item.clientVisible,
                                          assigneeUserId: item.assigneeUserId ?? null,
                                          approvers: (item.approvers ?? []).map((approver) => ({
                                            contactId: approver.contactId,
                                            obligation: approver.obligation,
                                            decision: approver.decision,
                                            contactName: approver.contactName,
                                          })),
                                        });
                                      }}
                                    />
                                    <DeleteIconButton
                                      onClick={() =>
                                        void handleDelete(item.title, () => deleteProjectBacklogItem(projectId, item.id))
                                      }
                                    />
                                  </>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <DraggableModalPanel
              open={linkPanelItem != null}
              title="Planejar vínculo"
              subtitle={linkPanelItem?.title}
              onClose={() => setLinkPanelItemId(null)}
              className="w-full max-w-2xl"
            >
              {linkPanelItem ? (
                <ProjectBacklogExternalLinksPanel
                  key={linkPanelItem.id}
                  projectId={projectId}
                  backlogItemId={linkPanelItem.id}
                  backlogTitle={linkPanelItem.title}
                  links={linkPanelItem.externalLinks ?? []}
                  canWrite={canWrite}
                  onChanged={() => void loadItems()}
                />
              ) : null}
            </DraggableModalPanel>
          </>
        );
      }

      case 'roadmap':
        if (roadmapItems.length === 0) {
          return <InlineAlert>Nenhum item de roadmap. Use o formulário acima para cadastrar o primeiro.</InlineAlert>;
        }
        return (
          <div className="erp-table-wrap">
            <div className="erp-table-scroll">
              <table className="erp-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Tipo</th>
                    <th>Status</th>
                    <th>Data</th>
                    {canWrite ? <th className="text-right">Ações</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {roadmapItems.map((item) => (
                    <tr key={item.id}>
                      <td className="max-w-md font-medium text-secondary-900" title={item.title}>
                        {truncateText(item.title, 200)}
                      </td>
                      <td className="whitespace-nowrap">{labelFor(ROADMAP_KIND_OPTIONS, item.kind)}</td>
                      <td className="whitespace-nowrap">{labelFor(ROADMAP_STATUS_OPTIONS, item.status)}</td>
                      <td className="whitespace-nowrap">{formatDateBr(item.plannedDateUtc)}</td>
                      {canWrite ? (
                        <td>
                          <div className="flex justify-end gap-1">
                            <EditIconButton
                              onClick={() => {
                                setEditingId(item.id);
                                expandForm();
                                setRoadmapForm({
                                  title: item.title,
                                  description: item.description ?? '',
                                  kind: item.kind,
                                  status: item.status,
                                  plannedDateUtc: item.plannedDateUtc,
                                  sortOrder: item.sortOrder,
                                });
                              }}
                            />
                            <DeleteIconButton
                              onClick={() =>
                                void handleDelete(item.title, () => deleteProjectRoadmapItem(projectId, item.id))
                              }
                            />
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'requisitos':
        if (requirements.length === 0) {
          return <InlineAlert>Nenhum requisito cadastrado. Use o formulário acima para registrar o primeiro.</InlineAlert>;
        }
        return (
          <div className="erp-table-wrap">
            <div className="erp-table-scroll">
              <table className="erp-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Validação</th>
                    <th>Entregas</th>
                    {canWrite ? <th className="text-right">Ações</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {requirements.map((item) => (
                    <tr key={item.id}>
                      <td className="max-w-md font-medium text-secondary-900" title={item.title}>
                        {truncateText(item.title, 200)}
                      </td>
                      <td className="max-w-md" title={item.validationCriteria}>
                        {truncateText(item.validationCriteria, 200)}
                      </td>
                      <td className="whitespace-nowrap">{item.backlogItemCount}</td>
                      {canWrite ? (
                        <td>
                          <div className="flex justify-end gap-1">
                            <EditIconButton
                              onClick={() => {
                                setEditingId(item.id);
                                expandForm();
                                setRequirementForm({
                                  title: item.title,
                                  validationCriteria: item.validationCriteria,
                                });
                              }}
                            />
                            <DeleteIconButton
                              onClick={() =>
                                void handleDelete(item.title, () => deleteProjectRequirement(projectId, item.id))
                              }
                            />
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'riscos':
        if (risks.length === 0) {
          return <InlineAlert>Nenhum risco cadastrado. Use o formulário acima para registrar o primeiro.</InlineAlert>;
        }
        return (
          <div className="erp-table-wrap">
            <div className="erp-table-scroll">
              <table className="erp-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Probabilidade</th>
                    <th>Impacto</th>
                    <th>Status</th>
                    {canWrite ? <th className="text-right">Ações</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {risks.map((item) => (
                    <tr key={item.id}>
                      <td className="max-w-md font-medium text-secondary-900" title={item.title}>
                        {truncateText(item.title, 200)}
                      </td>
                      <td className="whitespace-nowrap">{labelFor(RISK_PROBABILITY_OPTIONS, item.probability)}</td>
                      <td className="whitespace-nowrap">{labelFor(RISK_IMPACT_OPTIONS, item.impact)}</td>
                      <td className="whitespace-nowrap">{labelFor(RISK_STATUS_OPTIONS, item.status)}</td>
                      {canWrite ? (
                        <td>
                          <div className="flex justify-end gap-1">
                            <EditIconButton
                              onClick={() => {
                                setEditingId(item.id);
                                expandForm();
                                setRiskForm({
                                  title: item.title,
                                  description: item.description ?? '',
                                  probability: item.probability,
                                  impact: item.impact,
                                  treatmentPriority: item.treatmentPriority,
                                  mitigationPlan: item.mitigationPlan ?? '',
                                  contingencyPlan: item.contingencyPlan ?? '',
                                  status: item.status,
                                  identifiedAtUtc: item.identifiedAtUtc,
                                });
                              }}
                            />
                            <DeleteIconButton
                              onClick={() =>
                                void handleDelete(item.title, () => deleteProjectRisk(projectId, item.id))
                              }
                            />
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'problemas':
        if (issues.length === 0) {
          return <InlineAlert>Nenhum problema cadastrado. Use o formulário acima para registrar o primeiro.</InlineAlert>;
        }
        return (
          <div className="erp-table-wrap">
            <div className="erp-table-scroll">
              <table className="erp-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Severidade</th>
                    <th>Status</th>
                    {canWrite ? <th className="text-right">Ações</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {issues.map((item) => (
                    <tr key={item.id}>
                      <td className="max-w-md font-medium text-secondary-900" title={item.title}>
                        {truncateText(item.title, 200)}
                      </td>
                      <td className="whitespace-nowrap">{labelFor(ISSUE_SEVERITY_OPTIONS, item.severity)}</td>
                      <td className="whitespace-nowrap">{labelFor(ISSUE_STATUS_OPTIONS, item.status)}</td>
                      {canWrite ? (
                        <td>
                          <div className="flex justify-end gap-1">
                            <EditIconButton
                              onClick={() => {
                                setEditingId(item.id);
                                expandForm();
                                setIssueForm({
                                  title: item.title,
                                  description: item.description ?? '',
                                  severity: item.severity,
                                  status: item.status,
                                  correctiveAction: item.correctiveAction ?? '',
                                  identifiedAtUtc: item.identifiedAtUtc,
                                });
                              }}
                            />
                            <DeleteIconButton
                              onClick={() =>
                                void handleDelete(item.title, () => deleteProjectIssue(projectId, item.id))
                              }
                            />
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'mudancas':
        if (changes.length === 0) {
          return <InlineAlert>Nenhuma solicitação de mudança. Use o formulário acima para cadastrar a primeira.</InlineAlert>;
        }
        return (
          <div className="erp-table-wrap">
            <div className="erp-table-scroll">
              <table className="erp-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Status</th>
                    <th>Motivo</th>
                    {canWrite ? <th className="text-right">Ações</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {changes.map((item) => (
                    <tr key={item.id}>
                      <td className="max-w-md font-medium text-secondary-900" title={item.title}>
                        {truncateText(item.title, 200)}
                      </td>
                      <td className="whitespace-nowrap">{CHANGE_STATUS_LABELS[item.status] ?? item.status}</td>
                      <td className={item.rejectionReason?.trim() ? 'text-secondary-800' : 'text-secondary-400'}>
                        {item.rejectionReason?.trim() || '—'}
                      </td>
                      {canWrite ? (
                        <td>
                          <div className="flex flex-wrap items-center justify-end gap-1">
                            {item.status === 'Draft' ? (
                              <button
                                type="button"
                                className="rounded border border-secondary-300 px-2 py-0.5 text-xs hover:bg-secondary-50"
                                onClick={() => void handleChangeAction(item.id, 'submit')}
                              >
                                Submeter
                              </button>
                            ) : null}
                            {item.status === 'UnderReview' ? (
                              <>
                                <button
                                  type="button"
                                  className="rounded border border-green-300 px-2 py-0.5 text-xs text-green-800 hover:bg-green-50"
                                  onClick={() => void handleChangeAction(item.id, 'approve')}
                                >
                                  Aprovar
                                </button>
                                <button
                                  type="button"
                                  className="rounded border border-red-300 px-2 py-0.5 text-xs text-red-800 hover:bg-red-50"
                                  onClick={() => void handleChangeAction(item.id, 'reject')}
                                >
                                  Rejeitar
                                </button>
                              </>
                            ) : null}
                            {item.status === 'Draft' ? (
                              <>
                                <EditIconButton
                                  onClick={() => {
                                    setEditingId(item.id);
                                    expandForm();
                                    setChangeForm({
                                      title: item.title,
                                      description: item.description ?? '',
                                      impactScope: item.impactScope ?? '',
                                      impactSchedule: item.impactSchedule ?? '',
                                      impactCost: item.impactCost ?? '',
                                    });
                                  }}
                                />
                                <DeleteIconButton
                                  onClick={() =>
                                    void handleDelete(item.title, () => deleteProjectChangeRequest(projectId, item.id))
                                  }
                                />
                              </>
                            ) : null}
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'reunioes':
        if (meetings.length === 0) {
          return <InlineAlert>Nenhuma reunião cadastrada. Use o formulário acima para agendar a primeira.</InlineAlert>;
        }
        return (
          <>
          <div className="erp-table-wrap">
            <div className="erp-table-scroll">
              <table className="erp-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Tipo</th>
                    <th>Data</th>
                    <th className="text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {meetings.map((item) => {
                    const typeLabel = labelFor(MEETING_TYPE_OPTIONS, item.meetingType);
                    const typeText = item.meetingType === 'Other' && item.otherTypeNote?.trim()
                      ? `${typeLabel} (${item.otherTypeNote.trim()})`
                      : typeLabel;
                    return (
                      <tr key={item.id}>
                        <td className="max-w-md font-medium text-secondary-900" title={item.title}>
                          {truncateText(item.title, 200)}
                        </td>
                        <td>{typeText}</td>
                        <td className="whitespace-nowrap">{formatMeetingDateTime(item.scheduledAtUtc)}</td>
                        <td>
                          <div className="flex justify-end gap-1">
                            <MeetingParticipantsIconButton
                              onClick={() => setParticipantsMeetingId(item.id)}
                            />
                            {canWrite ? (
                              <>
                                <EditIconButton
                                  onClick={() => {
                                    setEditingId(item.id);
                                    expandForm();
                                    setMeetingForm({
                                      title: item.title,
                                      meetingType: item.meetingType,
                                      otherTypeNote: item.otherTypeNote ?? '',
                                      scheduledAtUtc: item.scheduledAtUtc,
                                      scheduledAtLocal: toLocalDateTimeInputValue(item.scheduledAtUtc),
                                      durationMinutes: item.durationMinutes,
                                      locationOrLink: item.locationOrLink ?? '',
                                      minutes: item.minutes ?? '',
                                      stakeholderIds: (item.participants ?? []).map((person) => person.stakeholderId),
                                    });
                                  }}
                                />
                                <DeleteIconButton
                                  onClick={() =>
                                    void handleDelete(item.title, () => deleteProjectMeeting(projectId, item.id))
                                  }
                                />
                              </>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <MeetingParticipantsDialog
            projectId={projectId}
            meeting={meetings.find((item) => item.id === participantsMeetingId) ?? null}
            canWrite={canWrite}
            stakeholders={stakeholders}
            onClose={() => setParticipantsMeetingId(null)}
            onStakeholderCreated={(stakeholder) =>
              setStakeholders((current) =>
                current.some((person) => person.id === stakeholder.id) ? current : [...current, stakeholder])
            }
            onUpdated={(updated) => {
              setMeetings((current) =>
                current.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)),
              );
              if (editingId === updated.id) {
                setMeetingForm((current) => ({
                  ...current,
                  stakeholderIds: (updated.participants ?? []).map((person) => person.stakeholderId),
                }));
              }
            }}
          />
          </>
        );

      default:
        return null;
    }
  }

  return (
    <div className="space-y-4">
      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? TAB_FORM_TITLES[activeTab].edit : TAB_FORM_TITLES[activeTab].create}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          editingSummary={
            editingId ? 'Editando registro — o formulário abriu em uma janela' : undefined
          }
          createSummary={TAB_FORM_TITLES[activeTab].summary}
          isEditing={Boolean(editingId)}
          className="mb-0"
          panelClassName={
            activeTab === 'reunioes'
              ? 'w-full max-w-5xl min-h-[calc(100dvh-2rem)]'
              : 'w-full max-w-5xl'
          }
        >
          <form className="px-4 pb-4 pt-3" onSubmit={(event) => void handleSubmit(event)}>
            {renderForm()}
            <FormActionBar
              onCancel={() => collapseForm(resetForm)}
              cancelLabel="Cancelar"
              className="erp-btn-actions mt-4"
            >
              <button type="submit" className="erp-btn-primary" disabled={saving}>
                {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
              </button>
            </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : null}

      <PageCard>
        {loading ? <LoadingSpinner label="Carregando..." /> : renderList()}
      </PageCard>
    </div>
  );
}
